<template>
  <hel-module title="企业经营核心指标" :class="scope.clsPrefix" v-loading="loading">
    <div class="overview-container">
      <div class="overview-main">
        <div
          class="overview-item"
          v-for="(item, index) in indicators"
          :key="item.name"
          @mouseenter="hoverIndex = index"
          @mouseleave="hoverIndex = -1"
        >
          <div class="item-header">
            <span class="item-name">{{ item.name }}</span>
            <span :class="['item-trend', item.trendClass]">{{ item.trendValue }}</span>
          </div>
          <div class="item-value">
            <span class="value-num">{{ item.value }}</span>
            <span class="value-unit">{{ item.unit }}</span>
          </div>
          <div class="item-desc">{{ item.desc }}</div>
          <div class="item-mask" v-show="hoverIndex === index">
            <button class="smart-analysis-btn" @click.stop="handleSmartAnalysis(item)">
              智能分析
            </button>
          </div>
        </div>
      </div>
    </div>
  </hel-module>
</template>

<script>
import HelModule from '../common/module/index.vue'
import { getSalesOverview } from '@/api/sales'

// 企业经营核心指标定义：key / trendKey 对应概览数据返回字段
// goodWhenUp：趋势上升是否代表业务好转，决定颜色（与正负号无关）
// trendDecimals：趋势值小数位；trendUnit：趋势值后缀（% / pp / 空）
// content：点击「智能分析」时向大模型发送的问题原文（保持原文，不做拼接）
const METRICS = [
  {
    name: '在手商机金额',
    unit: '万',
    key: 'opportunityAmount',
    trendKey: 'opportunityTrend',
    fmt: 'number',
    trendDecimals: 1,
    trendUnit: '%',
    goodWhenUp: true,
    desc: '15个商机仍在推进',
    content: '请分析「在手商机金额」环比变动的构成原因，区分商机数量变化与单笔金额变化的影响，并按阶段、客户、负责人定位主要贡献来源，指出需要重点关注的项目与风险。'
  },
  {
    name: '本月签约额',
    unit: '万',
    key: 'monthSignAmount',
    trendKey: 'monthSignTrend',
    fmt: 'number',
    trendDecimals: 1,
    trendUnit: '%',
    goodWhenUp: true,
    desc: '已签合同金额',
    content: '请分析「本月签约额」的完成情况与目标缺口来源，拆解到具体合同与客户，区分新签、续签与延期签署的影响，并给出追平目标的关键动作。'
  },
  {
    name: '本月回款额',
    unit: '万',
    key: 'monthPaymentAmount',
    trendKey: 'monthPaymentTrend',
    fmt: 'number',
    trendDecimals: 1,
    trendUnit: '%',
    goodWhenUp: true,
    desc: '已到账金额',
    content: '请分析「本月回款额」的构成与变化原因，区分合同节点款、预付款与逾期回款，按客户定位主要贡献与风险，并给出对账与催收建议。'
  },
  {
    name: '本月目标达成率',
    unit: '%',
    key: 'targetAchieveRate',
    trendKey: 'targetAchieveTrend',
    fmt: 'decimal',
    trendDecimals: 1,
    trendUnit: 'pp',
    goodWhenUp: true,
    desc: '目标 280万',
    content: '请分析「本月目标达成率」的进度是否正常，拆解缺口来源到具体在谈合同，判断哪些项目可以在本月内转化为签约，并给出优先级建议。'
  },
  {
    name: '报价中金额',
    unit: '万',
    key: 'quotingAmount',
    trendKey: 'quotingTrend',
    fmt: 'decimal',
    trendDecimals: 1,
    trendUnit: '%',
    goodWhenUp: true,
    desc: '3个已出价项目，另1个待测算',
    content: '请分析「报价中金额」的结构与变化原因，按报价状态与停留时长分解，找出停滞项目和待确认项目，并给出推进顺序。'
  },
  {
    name: '交付风险订单',
    unit: '个',
    key: 'deliveryRiskOrders',
    trendKey: 'deliveryRiskTrend',
    fmt: 'number',
    trendDecimals: 0,
    trendUnit: '',
    goodWhenUp: false,
    desc: '影响金额约 700 万',
    content: '请分析「交付风险订单」的构成与变化，按风险类型、责任部门与影响金额分解，区分供应端与产能端原因，并给出处置优先级。'
  }
]

export default {
  name: 'SalesCoreIndicators',
  components: {
    HelModule
  },
  props: {
    data: {
      type: Object,
      default() {
        return {}
      }
    }
  },
  data() {
    return {
      loading: false,
      scope: {
        clsPrefix: 'rvc-cosmoplat-workspace-sales-core-indicators'
      },
      hoverIndex: -1,
      // 初始占位：数据未返回或字段缺失时统一显示 --
      indicators: METRICS.map((metric) => ({
        name: metric.name,
        unit: metric.unit,
        desc: metric.desc,
        content: metric.content,
        value: '--',
        trendValue: '--',
        trendClass: 'trend-flat'
      }))
    }
  },
  created() {
    this.fetchCoreMetrics()
  },
  methods: {
    handleSmartAnalysis(item) {
      // 携带该指标的问题原文 content，宿主命中 type=salesKpi 白名单分支后原文直发
      this.$emit('smart-analysis', { ...item, type: 'salesKpi' })
    },
    isBlank(value) {
      return value === null || value === undefined || value === '' || isNaN(Number(value))
    },
    formatNumber(value) {
      if (this.isBlank(value)) return '--'
      return Number(value).toLocaleString('zh-CN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
    },
    formatDecimal(value) {
      if (this.isBlank(value)) return '--'
      return Number(value).toFixed(1)
    },
    // 趋势文案为「±x.x% / ±x.xpp / ±n」；颜色按业务语义：好转为绿、恶化为红，与正负号无关
    buildTrend(metric, trendValue) {
      if (this.isBlank(trendValue)) {
        return { trendValue: '--', trendClass: 'trend-flat' }
      }
      const num = Number(trendValue)
      const text = (num > 0 ? '+' : '') + num.toFixed(metric.trendDecimals) + metric.trendUnit
      if (num === 0) {
        return { trendValue: text, trendClass: 'trend-flat' }
      }
      const isGood = num > 0 ? metric.goodWhenUp : !metric.goodWhenUp
      return { trendValue: text, trendClass: isGood ? 'trend-good' : 'trend-bad' }
    },
    // 数据来源：销售概览指标接口，返回概览数据对象
    async fetchOverview() {
      const res = await getSalesOverview()
      if (!res || res.code !== 200 || !res.data) {
        return null
      }
      return res.data
    },
    async fetchCoreMetrics() {
      this.loading = true
      try {
        const m = await this.fetchOverview()
        if (!m) {
          return
        }
        this.indicators = METRICS.map((metric) => {
          const trend = this.buildTrend(metric, m[metric.trendKey])
          return {
            name: metric.name,
            unit: metric.unit,
            desc: metric.desc,
            content: metric.content,
            value: metric.fmt === 'decimal' ? this.formatDecimal(m[metric.key]) : this.formatNumber(m[metric.key]),
            trendValue: trend.trendValue,
            trendClass: trend.trendClass
          }
        })
      } catch (error) {
        console.error('获取企业经营核心指标失败:', error)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$class-prefix: "rvc-cosmoplat-workspace-sales-core-indicators";
.overview-main {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 8px;
}
.#{$class-prefix} {
  overflow: hidden;
  .overview-container {
    height: 100%;
    width: 100%;
    scrollbar-width: thin;
    scrollbar-color: transparent;
  }
  .overview-container:hover {
    scrollbar-width: thin;
    scrollbar-color: #b0b0b0 #e8e8e8;
  }
  .overview-item {
    min-width: 120px;
    display: flex;
    flex-direction: column;
    border-radius: 8px;
    background: #eff2fc;
    padding: 10px 12px;
    position: relative;
    .item-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 4px;
      margin-bottom: 6px;
    }
    .item-name {
      font-size: 12px;
      font-family: PingFangSC-Medium;
      font-weight: 600;
      color: #333333;
      white-space: nowrap;
    }
    .item-trend {
      font-size: 12px;
      font-family: PingFangSC-Regular;
      font-weight: 500;
      white-space: nowrap;
      // 好转
      &.trend-good {
        color: #73b950;
      }
      // 恶化
      &.trend-bad {
        color: #f44f4f;
      }
      // 持平 / 无数据
      &.trend-flat {
        color: #909399;
      }
    }
    .item-value {
      display: flex;
      align-items: baseline;
      gap: 6px;
    }
    .value-num {
      font-size: 24px;
      font-weight: 700;
      font-family: PingFangSC-Semibold;
      color: #333;
      line-height: 32px;
    }
    .value-unit {
      font-size: 12px;
      color: #333;
      font-weight: 400;
    }
    .item-desc {
      margin-top: auto;
      padding-top: 6px;
      font-size: 12px;
      font-family: PingFangSC-Regular;
      font-weight: 400;
      color: #909399;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .item-mask {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: #edf1fb3b;
      backdrop-filter: blur(1px);
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .smart-analysis-btn {
      width: 64px;
      height: 24px;
      border: none;
      background-image: linear-gradient(270deg, #4a5ff3 0%, #4e86ff 100%);
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      font-family: PingFangSC-Regular;
      font-weight: 400;
      font-size: 12px;
      color: #ffffff;
      line-height: 12px;
    }
  }
}
@media screen and (max-width: 1440px) {
  .#{$class-prefix} {
    .overview-item {
      padding: 8px 10px;
      .value-num {
        font-size: 18px;
      }
      .item-name {
        font-size: 11px;
      }
      .item-trend {
        font-size: 11px;
      }
      .item-desc {
        font-size: 11px;
      }
    }
  }
}
</style>

<style lang="scss">
.rvc-cosmoplat-workspace-sales-core-indicators .overview-container::-webkit-scrollbar {
  height: 0;
}
.rvc-cosmoplat-workspace-sales-core-indicators
  .overview-container:hover::-webkit-scrollbar {
  height: 6px;
}
.rvc-cosmoplat-workspace-sales-core-indicators
  .overview-container::-webkit-scrollbar-track {
  background: #e8e8e8;
  border-radius: 3px;
}
.rvc-cosmoplat-workspace-sales-core-indicators
  .overview-container::-webkit-scrollbar-thumb {
  background: #b0b0b0;
  border-radius: 3px;
}
.rvc-cosmoplat-workspace-sales-core-indicators
  .overview-container::-webkit-scrollbar-thumb:hover {
  background: #909090;
}
</style>
